import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  IsActiveMatchOptions,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from "@angular/router";
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from "rxjs";

import { AccentThemeStore } from "../accent-theme/accent-theme.store";
import type { SessionActor } from "../auth";
import { AuthService, AuthStore } from "../auth";
import { InventoryService } from "../../features/inventory/inventory.service";
import type { AutocompleteResult } from "../../features/inventory/models/inventory.model";
import { resolveSearchNavigationCommands } from "./search-navigation";
import { AccountMenuController } from "./account-menu.controller";

export const canAccessTransfersForActor = (
  actor: SessionActor | null,
): boolean => actor !== null;
export const canAccessManagementForActor = (
  actor: SessionActor | null,
): boolean => actor !== null;
export const canAccessAdminNavigationForActor = (
  actor: SessionActor | null,
): boolean => {
  return actor?.role === "SUPER_ADMIN" || actor?.role === "STORE_ADMIN";
};

interface ShellNavLink {
  id: string;
  label: string;
  path: string;
  activePaths?: readonly string[];
  exact?: boolean;
  isVisible: (actor: SessionActor | null) => boolean;
}

export const SHELL_NAV_LINKS: readonly ShellNavLink[] = [
  {
    id: "products",
    label: "Productos",
    path: "/",
    exact: true,
    isVisible: canAccessManagementForActor,
  },
  {
    id: "stores",
    label: "Locales",
    path: "/stores",
    isVisible: canAccessManagementForActor,
  },
  {
    id: "management",
    label: "Gestión",
    path: "/management",
    activePaths: [
      "/users",
      "/suppliers",
      "/custom-attributes",
      "/transfers",
      "/business-settings",
    ],
    isVisible: canAccessManagementForActor,
  },
];

interface ShellBusinessOption {
  id: string;
  label: string;
}

const SEARCH_DEBOUNCE_MS = 300;

const searchResultTypeLabel: Record<AutocompleteResult["type"], string> = {
  product: "producto",
  variant: "variante",
  unit: "unidad",
};

const resolveSearchErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return "No pudimos buscar en el inventario. Intentá de nuevo.";
  }

  return "No pudimos buscar en el inventario. Intentá de nuevo.";
};

@Component({
  selector: "us-app-shell",
  standalone: true,
  imports: [CommonModule, RouterLink, RouterOutlet],
  template: `
    <div class="shell">
      <aside class="shell__sidebar">
        <div class="shell__brand">
          <img
            class="shell__brand-logo"
            src="/public/images/logo_principal_rojo.svg"
            alt="Ultra Móviles"
          />
        </div>

        <nav class="shell__nav">
          @for (link of visibleNavLinks(); track link.id) {
            <a
              [routerLink]="link.path"
              [class.is-active]="isNavLinkActive(link)"
              [attr.aria-current]="isNavLinkActive(link) ? 'page' : null"
            >
              {{ link.label }}
            </a>
          }
        </nav>

        <div class="shell__sidebar-footer">
          <button
            #accountTrigger
            class="shell__account"
            type="button"
            [attr.aria-expanded]="accountMenuController.isOpen()"
            (click)="accountMenuController.toggle($event)"
          >
            <span class="shell__avatar">{{ userInitials() }}</span>

            <span class="shell__account-copy">
              <strong>{{ displayName() }}</strong>
              <small>{{ roleLabel() }}</small>
            </span>

            <span class="shell__account-chevron">⌄</span>
          </button>

          @if (accountMenuController.isOpen()) {
            <div #accountMenu class="shell__account-menu">
              @if (canPickAccentColor()) {
                <label
                  class="shell__account-menu-accent shell__account-menu-action shell__account-menu-action--secondary"
                >
                  Color de acento
                  <input
                    type="color"
                    class="shell__account-menu-color-picker"
                    [value]="accentThemeStore.accent()"
                    (input)="handleAccentInput($event)"
                  />
                </label>
              }
              <button
                class="shell__account-menu-action shell__account-menu-action--secondary"
                type="button"
                (click)="navigateToChangePassword()"
              >
                Cambiar contraseña
              </button>
              <button
                class="shell__account-menu-action"
                type="button"
                [disabled]="signingOut()"
                (click)="signOut()"
              >
                {{ signingOut() ? "Cerrando…" : "Cerrar sesión" }}
              </button>
            </div>
          }
        </div>
      </aside>

      <main class="shell__content">
        <header class="shell__header">
          <div class="shell__toolbar">
            <label class="shell__search" aria-label="Buscar en inventario">
              <input
                type="search"
                #searchInput
                [value]="searchQuery()"
                placeholder="Buscar productos, variantes o unidades"
                (input)="updateSearch($any($event.target).value ?? '')"
                (focus)="handleSearchFocus()"
                (blur)="handleSearchBlur()"
                (keydown)="handleSearchKeydown($event)"
                aria-autocomplete="list"
                [attr.aria-expanded]="searchDropdownOpen()"
                aria-controls="header-search-results"
              />
              <span class="shell__search-icon">⌕</span>

              @if (!searchDropdownOpen()) {
                <span class="shell__search-hint">
                  <kbd>Ctrl</kbd> + <kbd>K</kbd>
                </span>
              }

              @if (searchDropdownOpen()) {
                <div
                  class="shell__search-dropdown"
                  id="header-search-results"
                  role="listbox"
                >
                  @if (searchLoading()) {
                    <p class="shell__search-status">Buscando…</p>
                  } @else {
                    @if (searchError(); as errorMessage) {
                      <p
                        class="shell__search-status shell__search-status--error"
                        role="alert"
                      >
                        {{ errorMessage }}
                      </p>
                    } @else if (
                      searchResults().length === 0 &&
                      searchQuery().trim().length >= 3
                    ) {
                      <p class="shell__search-status">Sin resultados</p>
                    } @else {
                      @for (
                        result of searchResults();
                        track result.type +
                          "-" +
                          (result.type === "product"
                            ? result.productId
                            : result.type === "variant"
                              ? result.variantId
                              : result.unitId);
                        let index = $index
                      ) {
                        <button
                          type="button"
                          class="shell__search-option"
                          [class.is-highlighted]="
                            index === highlightedSearchIndex()
                          "
                          (mousedown)="handleSearchResultMouseDown()"
                          (mouseenter)="setHighlightedSearchIndex(index)"
                          (click)="selectSearchResult(result)"
                          [attr.aria-selected]="
                            index === highlightedSearchIndex()
                          "
                        >
                          <span class="shell__search-option-copy">
                            <strong>{{ result.label }}</strong>
                            @if (
                              result.type === "product" && result.description
                            ) {
                              <small>{{ result.description }}</small>
                            } @else if (result.type === "unit") {
                              <small>
                                {{
                                  result.imei ||
                                    result.serialNumber ||
                                    result.storeName
                                }}
                              </small>
                            } @else if (result.type === "variant") {
                              <small
                                >{{ result.color }} ·
                                {{ result.capacity }}</small
                              >
                            }
                          </span>
                          <span class="shell__search-option-type">{{
                            searchResultType(result)
                          }}</span>
                        </button>
                      }
                    }
                  }
                </div>
              }
            </label>

            @if (!isSuperAdmin()) {
              <label
                class="shell__business-selector"
                aria-label="Seleccionar negocio"
              >
                <select
                  #businessSelector
                  [value]="currentBusinessId()"
                  [disabled]="
                    switchingStore() || availableBusinesses().length <= 1
                  "
                  (change)="
                    updateSelectedBusiness($any($event.target).value ?? '')
                  "
                >
                  @for (business of availableBusinesses(); track business.id) {
                    <option [value]="business.id">{{ business.label }}</option>
                  }
                </select>
              </label>
            }
          </div>

          <div class="shell__actions">
            <p
              class="shell__switch-error"
              *ngIf="storeSwitchError() as errorMessage"
              role="alert"
              aria-live="assertive"
            >
              {{ errorMessage }}
            </p>
            <p
              class="shell__logout-error"
              *ngIf="logoutError() as errorMessage"
              role="alert"
              aria-live="assertive"
            >
              {{ errorMessage }}
            </p>
          </div>
        </header>

        <section class="shell__page">
          <router-outlet />
        </section>
      </main>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        min-height: 100vh;
      }

      .shell {
        display: grid;
        grid-template-columns: 248px 1fr;
        height: 100vh;
        background: #f5f3f1;
        overflow: hidden;
      }

      .shell__sidebar {
        display: flex;
        flex-direction: column;
        gap: 24px;
        height: 100vh;
        padding: 24px 18px 18px;
        background: #ffffff;
        border-right: 1px solid #ece7e2;
        color: #332b28;
      }

      .shell__brand {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .shell__brand h1 {
        margin: 0;
      }

      .shell__brand-logo {
        display: block;
        width: 118px;
        height: auto;
      }

      .shell__subtitle {
        margin: 8px 0 0;
        color: #7a6f69;
        font-size: 0.9rem;
      }

      .shell__nav {
        display: grid;
        gap: 10px;
      }

      .shell__nav a {
        padding: 10px 14px;
        border: 1px solid #e8e1dc;
        border-radius: 10px;
        background: #ffffff;
        color: #5c514b;
        text-decoration: none;
        font-weight: 600;
        transition:
          background-color 120ms ease,
          color 120ms ease,
          border-color 120ms ease,
          box-shadow 120ms ease;
      }

      .shell__nav a.is-active {
        border-color: #c81419;
        background: linear-gradient(180deg, #d71b21 0%, #b90f15 100%);
        color: #ffffff;
        box-shadow: 0 10px 24px rgba(200, 20, 25, 0.18);
      }

      .shell__sidebar-footer {
        margin-top: auto;
        position: relative;
      }

      .shell__account {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ece7e2;
        border-radius: 14px;
        background: #fbfaf9;
        color: inherit;
        cursor: pointer;
        text-align: left;
      }

      .shell__avatar {
        display: inline-grid;
        place-items: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #f2e5e2;
        color: #b90f15;
        font-size: 0.85rem;
        font-weight: 800;
      }

      .shell__account-copy {
        display: grid;
      }

      .shell__account-copy strong {
        font-size: 0.92rem;
      }

      .shell__account-copy small {
        color: #7a6f69;
      }

      .shell__account-chevron {
        color: #8d8078;
        font-size: 0.9rem;
      }

      .shell__account-menu {
        position: absolute;
        left: 0;
        right: 0;
        bottom: calc(100% + 8px);
        padding: 8px;
        border: 1px solid #ece7e2;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 16px 32px rgba(53, 36, 31, 0.14);
      }

      .shell__account-menu-action {
        width: 100%;
        padding: 10px 12px;
        border: 0;
        border-radius: 10px;
        background: #fff2f2;
        color: #b90f15;
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }

      .shell__account-menu-action--secondary {
        background: transparent;
        color: #5c514b;
        font-weight: 600;
      }

      .shell__account-menu-color-picker {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 0;
      }

      .shell__account-menu-accent {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }

      .shell__content {
        display: grid;
        grid-template-rows: auto 1fr;
        min-height: 0;
      }

      .shell__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 18px 24px;
        background: #f5f3f1;
        border-bottom: 1px solid #ece7e2;
      }

      .shell__toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
      }

      .shell__search {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 0 1 40%;
        width: 40%;
        min-width: 280px;
        padding: 0 12px;
        border: 1px solid #ddd4ce;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
      }

      .shell__search-icon {
        color: #8d8078;
        font-size: 2rem;
        line-height: 1;
      }

      .shell__search-hint {
        color: #8d8078;
        font-size: 0.72rem;
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        user-select: none;
      }

      .shell__search-hint kbd {
        display: inline-flex;
        align-items: center;
        padding: 1px 6px;
        border: 1px solid #ddd4ce;
        border-radius: 4px;
        background: #f7f5f4;
        font-family: inherit;
        font-size: 0.7rem;
        line-height: 1.4;
      }

      .shell__search input {
        width: 100%;
        border: 0;
        outline: none;
        padding: 11px 0;
        background: transparent;
        color: #332b28;
        font: inherit;
      }

      .shell__search-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        right: 0;
        display: grid;
        gap: 4px;
        padding: 8px;
        border: 1px solid #ece7e2;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 16px 32px rgba(53, 36, 31, 0.14);
        z-index: 10;
      }

      .shell__search-status {
        margin: 0;
        padding: 12px;
        color: #7a6f69;
        font-size: 0.92rem;
      }

      .shell__search-status--error {
        color: #b91c1c;
      }

      .shell__search-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        padding: 10px 12px;
        border: 0;
        border-radius: 10px;
        background: transparent;
        color: inherit;
        text-align: left;
        cursor: pointer;
      }

      .shell__search-option.is-highlighted,
      .shell__search-option:hover {
        background: #f7f3f1;
      }

      .shell__search-option-copy {
        display: grid;
        gap: 2px;
      }

      .shell__search-option-copy strong {
        font-size: 0.94rem;
      }

      .shell__search-option-copy small,
      .shell__search-option-type {
        color: #8d8078;
        font-size: 0.77rem;
      }

      .shell__search-option-type {
        text-transform: lowercase;
        white-space: nowrap;
      }

      .shell__business-selector {
        display: inline-flex;
        align-items: center;
        margin-left: auto;
        min-width: 210px;
        padding: 0 12px;
        border: 1px solid #ddd4ce;
        border-radius: 10px;
        background: #ffffff;
      }

      .shell__business-selector select {
        width: 100%;
        border: 0;
        outline: none;
        padding: 11px 0;
        background: transparent;
        color: #332b28;
        font: inherit;
      }

      .shell__actions {
        display: grid;
        justify-items: end;
        gap: 8px;
      }

      .shell__logout-error {
        margin: 0;
        color: #b91c1c;
        font-size: 0.875rem;
      }

      .shell__switch-error {
        margin: 0;
        color: #b91c1c;
        font-size: 0.875rem;
      }

      .shell__account-menu-action:disabled {
        cursor: wait;
        opacity: 0.7;
      }

      .shell__page {
        padding: 24px;
        min-height: 0;
        overflow-y: auto;
      }

      @media (max-width: 1100px) {
        .shell {
          grid-template-columns: 220px 1fr;
        }

        .shell__header,
        .shell__toolbar {
          flex-direction: column;
          align-items: stretch;
        }

        .shell__search,
        .shell__business-selector {
          width: 100%;
          flex: 1 1 100%;
          min-width: 0;
        }

        .shell__business-selector {
          margin-left: 0;
        }
      }
    `,
  ],
})
export class AppShellComponent implements AfterViewInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly inventoryService = inject(InventoryService);
  protected readonly accentThemeStore = inject(AccentThemeStore);
  private readonly signOutErrorMessage =
    "No pudimos cerrar la sesión. Intentá de nuevo.";
  private readonly switchStoreErrorMessage =
    "No pudimos cambiar el local activo. Intentá de nuevo.";
  private readonly searchInput$ = new Subject<string>();
  private searchBlurTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly businessSelectorRef =
    viewChild<ElementRef<HTMLSelectElement>>("businessSelector");
  private readonly searchInputRef =
    viewChild<ElementRef<HTMLInputElement>>("searchInput");

  readonly displayName = computed(
    () => this.authStore.user()?.name ?? "Invitado",
  );
  readonly roleLabel = computed(
    () => this.authStore.user()?.role.replaceAll("_", " ") ?? "Sin sesión",
  );
  private readonly subsetNavMatchOptions: IsActiveMatchOptions = {
    paths: "subset",
    queryParams: "ignored",
    matrixParams: "ignored",
    fragment: "ignored",
  };
  readonly exactNavMatchOptions: IsActiveMatchOptions = {
    paths: "exact",
    queryParams: "ignored",
    matrixParams: "ignored",
    fragment: "ignored",
  };
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  readonly visibleNavLinks = computed(() =>
    SHELL_NAV_LINKS.filter((link) => link.isVisible(this.authStore.actor())),
  );
  readonly canAccessStores = computed(() => this.authStore.isAuthenticated());
  readonly canAccessSuppliers = computed(() =>
    this.authStore.isAuthenticated(),
  );
  readonly canAccessTransfers = computed(() =>
    canAccessTransfersForActor(this.authStore.actor()),
  );
  readonly canAccessAdminNavigation = computed(() =>
    canAccessAdminNavigationForActor(this.authStore.actor()),
  );
  readonly canAccessUsers = computed(() =>
    canAccessAdminNavigationForActor(this.authStore.actor()),
  );
  readonly isSuperAdmin = computed(
    () => this.authStore.actor()?.role === "SUPER_ADMIN",
  );
  readonly userInitials = computed(() => {
    const name = this.displayName().trim();

    if (!name) {
      return "US";
    }

    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() ?? "")
      .join("");
  });
  readonly availableBusinesses = computed<ShellBusinessOption[]>(() => {
    const stores = this.authStore
      .storeMemberships()
      .filter((store) => store.isActive);

    if (stores.length === 0) {
      return [
        {
          id: "default-business",
          label: "Mi negocio",
        },
      ];
    }

    return stores.map((store) => ({
      id: store.id,
      label: store.name,
    }));
  });
  readonly currentBusinessId = computed(() => {
    const activeStoreId = this.authStore.activeStoreId();
    const availableBusinessIds = new Set(
      this.availableBusinesses().map((business) => business.id),
    );

    if (activeStoreId && availableBusinessIds.has(activeStoreId)) {
      return activeStoreId;
    }

    const selectedBusinessId = this.selectedBusinessId();

    if (selectedBusinessId && availableBusinessIds.has(selectedBusinessId)) {
      return selectedBusinessId;
    }

    return this.availableBusinesses()[0]?.id ?? "";
  });
  readonly searchQuery = signal("");
  readonly searchResults = signal<AutocompleteResult[]>([]);
  readonly searchLoading = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly searchDropdownOpen = signal(false);
  readonly highlightedSearchIndex = signal(-1);
  readonly selectedBusinessId = signal("");
  readonly signingOut = signal(false);
  readonly logoutError = signal<string | null>(null);
  readonly switchingStore = signal(false);
  readonly storeSwitchError = signal<string | null>(null);
  protected readonly accountMenuController = new AccountMenuController();
  protected readonly canPickAccentColor = computed(() =>
    this.authStore.isAuthenticated(),
  );

  constructor() {
    this.searchInput$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap((query) => {
          const trimmedQuery = query.trim();

          if (trimmedQuery.length < 3) {
            this.searchLoading.set(false);
            this.searchError.set(null);
            this.searchResults.set([]);
            this.highlightedSearchIndex.set(-1);
            this.searchDropdownOpen.set(false);
          } else {
            this.searchLoading.set(true);
            this.searchError.set(null);
            this.searchDropdownOpen.set(true);
          }
        }),
        switchMap((query) => {
          const trimmedQuery = query.trim();

          if (trimmedQuery.length < 3) {
            return of<{ data: AutocompleteResult[] }>({ data: [] });
          }

          return this.inventoryService.getStockAutocomplete(
            trimmedQuery,
            this.authStore.activeStoreId() ?? undefined,
          );
        }),
      )
      .subscribe({
        next: (response) => {
          this.searchLoading.set(false);
          this.searchResults.set(response.data);
          this.highlightedSearchIndex.set(response.data.length > 0 ? 0 : -1);
          this.searchDropdownOpen.set(this.searchQuery().trim().length >= 3);
        },
        error: (error: unknown) => {
          this.searchLoading.set(false);
          this.searchResults.set([]);
          this.highlightedSearchIndex.set(-1);
          this.searchError.set(resolveSearchErrorMessage(error));
          this.searchDropdownOpen.set(true);
        },
      });

    effect(() => {
      const activeStoreId = this.authStore.activeStoreId();
      const firstBusinessId = this.availableBusinesses()[0]?.id ?? "";
      const nextSelection = activeStoreId ?? firstBusinessId;

      if (nextSelection && this.selectedBusinessId() !== nextSelection) {
        this.selectedBusinessId.set(nextSelection);
      }

      requestAnimationFrame(() => {
        this.syncBusinessSelector();
      });
    });
  }

  ngAfterViewInit(): void {
    this.syncBusinessSelector();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();

    if (this.searchBlurTimeoutId !== null) {
      clearTimeout(this.searchBlurTimeoutId);
    }
  }

  updateSearch(query: string): void {
    this.searchQuery.set(query);
    this.searchError.set(null);
    this.searchInput$.next(query);
  }

  handleSearchFocus(): void {
    if (this.searchQuery().trim().length >= 3) {
      this.searchDropdownOpen.set(true);
    }
  }

  handleSearchBlur(): void {
    this.searchBlurTimeoutId = setTimeout(() => {
      this.closeSearchDropdown();
      this.searchBlurTimeoutId = null;
    }, 120);
  }

  handleSearchKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeSearchDropdown();
      return;
    }

    if (!this.searchDropdownOpen() || this.searchResults().length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.highlightedSearchIndex.update(
        (current) => (current + 1) % this.searchResults().length,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.highlightedSearchIndex.update((current) =>
        current <= 0 ? this.searchResults().length - 1 : current - 1,
      );
      return;
    }

    if (event.key === "Enter") {
      const highlightedResult =
        this.searchResults()[this.highlightedSearchIndex()];

      if (!highlightedResult) {
        return;
      }

      event.preventDefault();
      void this.selectSearchResult(highlightedResult);
    }
  }

  @HostListener("window:keydown", ["$event"])
  handleGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.searchInputRef()?.nativeElement.focus();
    }
  }

  handleSearchResultMouseDown(): void {
    if (this.searchBlurTimeoutId !== null) {
      clearTimeout(this.searchBlurTimeoutId);
      this.searchBlurTimeoutId = null;
    }
  }

  setHighlightedSearchIndex(index: number): void {
    this.highlightedSearchIndex.set(index);
  }

  async selectSearchResult(result: AutocompleteResult): Promise<void> {
    this.searchQuery.set("");
    this.searchResults.set([]);
    this.searchError.set(null);
    this.closeSearchDropdown();
    await this.router.navigate(resolveSearchNavigationCommands(result));
  }

  searchResultType(result: AutocompleteResult): string {
    return searchResultTypeLabel[result.type];
  }

  updateSelectedBusiness(businessId: string): void {
    if (
      !businessId ||
      businessId === this.authStore.activeStoreId() ||
      this.switchingStore()
    ) {
      this.selectedBusinessId.set(businessId);
      return;
    }

    this.storeSwitchError.set(null);
    this.switchingStore.set(true);
    this.authService
      .changeActiveStore(businessId)
      .pipe(take(1))
      .subscribe(
        (changed) => {
          this.switchingStore.set(false);

          if (!changed) {
            this.storeSwitchError.set(this.switchStoreErrorMessage);
            this.selectedBusinessId.set(this.authStore.activeStoreId() ?? "");
            return;
          }

          this.selectedBusinessId.set(
            this.authStore.activeStoreId() ?? businessId,
          );
        },
        () => {
          this.switchingStore.set(false);
          this.storeSwitchError.set(this.switchStoreErrorMessage);
          this.selectedBusinessId.set(this.authStore.activeStoreId() ?? "");
        },
      );
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.accountMenuController.isOpen()) {
      return;
    }
    if (this.accountMenuController.isOutsideClick(event)) {
      this.accountMenuController.close();
    }
  }

  @HostListener("window:keydown", ["$event"])
  handleWindowKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.accountMenuController.isOpen()) {
      this.accountMenuController.close();
    }
  }

  handleAccentInput(event: Event): void {
    const colorInput = event.target as HTMLInputElement | null;

    if (colorInput === null) {
      return;
    }

    this.accentThemeStore.setAccent(colorInput.value);
  }

  navigateToChangePassword(): void {
    this.accountMenuController.close();
    void this.router.navigate(["/users/me/change-password"]);
  }

  signOut(): void {
    if (this.signingOut()) {
      return;
    }

    this.accountMenuController.close();
    this.logoutError.set(null);
    this.signingOut.set(true);

    this.authService
      .signOut()
      .pipe(take(1))
      .subscribe((signedOut) => {
        this.signingOut.set(false);

        if (!signedOut) {
          this.logoutError.set(this.signOutErrorMessage);
          this.changeDetectorRef.detectChanges();
          return;
        }

        this.changeDetectorRef.detectChanges();
        void this.router.navigate(["/login"]);
      });
  }

  isNavLinkActive(link: ShellNavLink): boolean {
    this.currentUrl();
    const matchOptions = link.exact
      ? this.exactNavMatchOptions
      : this.subsetNavMatchOptions;
    const candidatePaths = [link.path, ...(link.activePaths ?? [])];

    return candidatePaths.some((path) =>
      this.router.isActive(path, matchOptions),
    );
  }

  private syncBusinessSelector(): void {
    const businessSelector = this.businessSelectorRef()?.nativeElement;

    if (!businessSelector) {
      return;
    }

    const currentBusinessId = this.currentBusinessId();

    if (businessSelector.value !== currentBusinessId) {
      businessSelector.value = currentBusinessId;
    }
  }

  private closeSearchDropdown(): void {
    this.searchDropdownOpen.set(false);
    this.highlightedSearchIndex.set(-1);
  }
}
